import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const MediterraneanADUDesignAtherton = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Mediterranean ADU Design & Construction in Atherton",
    "description": "Luxury Mediterranean ADUs in Atherton. Premium design & construction by UDA ADU. Elegant Mediterranean architecture for discerning homeowners.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/mediterranean-adu-design-atherton"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/mediterranean-adu-design-atherton" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Luxury Mediterranean ADU Design in Atherton
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Create an exquisite Mediterranean retreat on your Atherton estate. Our premium ADU designs blend authentic Mediterranean architecture with the luxury and sophistication expected in one of California's most prestigious communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Luxury Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Premium Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Luxury Mediterranean ADU Elements
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Handcrafted Terra Cotta</h3>
                  <p className="text-muted-foreground">Premium imported tiles and custom terra cotta details for authentic elegance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Natural Stone Accents</h3>
                  <p className="text-muted-foreground">Limestone, travertine, and marble details throughout</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Custom Wrought Iron</h3>
                  <p className="text-muted-foreground">Artisan-crafted ironwork designed exclusively for your property</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Reclaimed Wood Beams</h3>
                  <p className="text-muted-foreground">Authentic European reclaimed timber for ceilings and details</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Luxury Outdoor Spaces</h3>
                  <p className="text-muted-foreground">Private courtyards, terraces, and gardens with water features</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Atherton Estate Enhancement
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Estate Harmony</h3>
                <p className="text-muted-foreground">Mediterranean ADUs designed to complement your main residence and landscape</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Premium Investment</h3>
                <p className="text-muted-foreground">Luxury ADUs significantly enhance property value in Atherton's exclusive market</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Multi-Generational Living</h3>
                <p className="text-muted-foreground">Elegant separate living for family while maintaining privacy and luxury</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Guest House Sophistication</h3>
                <p className="text-muted-foreground">World-class accommodations for distinguished guests and visitors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Luxury Mediterranean ADU Process
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Estate Consultation</h3>
              <p className="text-muted-foreground">Comprehensive site analysis and luxury design planning</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Bespoke Design</h3>
              <p className="text-muted-foreground">Custom Mediterranean architecture tailored to your estate</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Premium Approvals</h3>
              <p className="text-muted-foreground">Navigate Atherton's stringent design review process</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Master Construction</h3>
              <p className="text-muted-foreground">Artisan craftsmanship with the finest materials</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-foreground">
            Create Your Mediterranean Masterpiece
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Design a luxury Mediterranean ADU that reflects the prestige and elegance of your Atherton estate. Our team specializes in creating architectural treasures that stand the test of time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Elite Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Luxury Estimate</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-foreground">Explore Luxury ADU Designs</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contemporary-adu-design-atherton" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Atherton
            </Link>
            <Link to="/spanish-colonial-adu-design-atherton" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Atherton
            </Link>
            <Link to="/craftsman-adu-design-atherton" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Atherton
            </Link>
            <Link to="/transitional-adu-design-atherton" className="text-[#FF7A00] hover:underline">
              Transitional ADUs Atherton
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default MediterraneanADUDesignAtherton;